import { useFloating, arrow, offset, flip, shift, autoUpdate } from '@floating-ui/react-dom';
import { useRef, useEffect } from 'react';
import { TOOLTIP_OFFSET_PX, WINDOW_MARGIN_PX } from './constants.js';

function usePosition(props, containerRef, active) {
  const arrowRef = useRef(null);
  const floating = useFloating({
    strategy: "fixed",
    placement: props.placement,
    open: active,
    transform: true,
    whileElementsMounted: active ? autoUpdate : undefined,
    middleware: props.middlewares ? [...props.middlewares, arrow({
      element: arrowRef
    })] : [offset(props.offsetPx ?? TOOLTIP_OFFSET_PX), flip({
      padding: WINDOW_MARGIN_PX,
      fallbackPlacements: props.disableFallbackPlacements ? [] : getFallbackPlacements(props.placement)
    }), shift({
      padding: WINDOW_MARGIN_PX
    }), arrow({
      element: arrowRef
    })]
  });
  useEffect(() => {
    if (active) {
      floating.refs.setReference(containerRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef.current, floating.refs, active]);
  return {
    floating,
    arrowRef
  };
}
const oppositeSideMap = {
  left: ["right", "top", "bottom"],
  right: ["left", "top", "bottom"],
  bottom: ["top", "right", "left"],
  top: ["bottom", "right", "left"]
};
const alignmentMap = {
  "": ["", "-start", "-end"],
  start: ["-start", "", "-end"],
  end: ["-end", "", "-start"]
};

/**
 * Ideally we want to flip the placement on the axis that has been used for
 * placement, if required. However, occasionally this will not be enough to
 * prevent overflow and as such we extend the list of available fallback
 * placements.
 *
 * @example "left" or "right" placements may both overflow on narrow devices.
 */
function getFallbackPlacements(placement) {
  const side = placement.match(/^(left|right|bottom|top)/g)?.[0];
  const alignment = placement.match(/-(start|end)$/g)?.[1] || "";
  if (!side) {
    return undefined;
  }
  const [opposite, ...rest] = oppositeSideMap[side];
  const alignments = alignmentMap[alignment];
  const alignedSides = alignments.map(it => `${side}${it}`).filter(it => it !== placement);
  const alignedOpposites = alignments.map(it => `${opposite}${it}`);
  return [...alignedSides, ...alignedOpposites, ...rest];
}

export { usePosition };
//# sourceMappingURL=hook.position.js.map
